<template>
  <div class="conversation-viewer">
    <div class="viewer-header">
      <div class="viewer-title-area">
        <h3 class="viewer-title">Conversation</h3>
        <span v-if="parsedConversation.length > 0" class="segment-count">({{ parsedConversation.length }} segments)</span>
      </div>
      <button class="toggle-btn" @click="toggleViewer">
        {{ isViewerExpanded ? '▼' : '▶' }}
      </button>
    </div>

    <div v-show="isViewerExpanded" class="viewer-content">
      <div v-if="parsedConversation.length > 0" class="conversation" ref="conversationContainer" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }">
        <transition-group name="fade" mode="out-in">
          <div
            v-for="(segment, idx) in parsedConversation"
            :key="idx"
            class="segment"
            :class="{
              active: idx === activeSegment,
              'segment-host': segment.role && segment.role.toLowerCase().includes('host'),
              'segment-expert': segment.role && !segment.role.toLowerCase().includes('host'),
              'is-playing': isPlaying && idx === activeSegment,
              'is-expanded': isExpandedView,
              'has-custom-recording': customRecordings.has(idx),
              'is-editing': editingSegmentIndex === idx
            }"
            :ref="el => setSegmentRef(el, messageId, idx)"
            @click="activateSegment(idx, $event)"
          >
            <section-type-editor
              v-if="!isPlaying && idx === activeSegment"
              :message-id="messageId"
              :segment-index="idx"
              :current-section="segment.section"
              @section-updated="handleSectionUpdated"
            />

            <div class="segment-editing-controls" v-if="!isPlaying && idx === activeSegment">
              <segment-editing-buttons
                :is-visible="true"
                :message-id="messageId"
                :segment-index="idx"
                :current-speaker="{
                  name: segment.speaker,
                  role: segment.role
                }"
                :current-section="segment.section"
                :segments="parsedConversation"
                @segment-deleted="handleSegmentDeleted"
                @segment-added="handleSegmentAdded"
              />

              <!-- AI Editing Buttons -->
              <div class="ai-editing-buttons" v-if="!editingSegmentIndex && !isCreatingSegment">
                <button
                  class="ai-edit-btn"
                  @click.stop="startEditingWithAI(idx)"
                  :disabled="!isPro || isSubmitting"
                >
                  <i class="icon-ai"></i> AI Edit
                </button>
                <div class="ai-insert-dropdown">
                  <button
                    class="ai-insert-btn"
                    @click.stop="openInsertDropdown(idx)"
                    :disabled="!isPro || isSubmitting"
                  >
                    <i class="icon-plus"></i> AI Insert
                  </button>
                  <div class="insert-dropdown-menu" v-if="activeInsertDropdown === idx">
                    <button
                      class="insert-option insert-before"
                      @click.stop="startCreatingWithAI(idx, 'before')"
                    >
                      Insert Before
                    </button>
                    <button
                      class="insert-option insert-after"
                      @click.stop="startCreatingWithAI(idx, 'after')"
                    >
                      Insert After
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="speaker-info">
              <span class="speaker-name" :style="{ color: getWaveBarColor(idx) }">{{ segment.speaker }}</span>
              <span class="segment-number">#{{ idx + 1 }}</span>
              <span class="speaker-role">({{ segment.role || 'Unknown' }})</span>

              <span v-if="customRecordings.has(idx)" class="custom-voice-badge" title="Custom voice recording">
                🎤
              </span>

              <!-- Edit indicator -->
              <span v-if="segment.edited" class="edited-badge" title="This segment has been edited">
                ✏️
              </span>

              <div v-if="isLoading && idx === activeSegment" class="loading-indicator">
                <div class="loading-spinner" :style="{ 'border-top-color': getWaveBarColor(idx) }"></div>
                <span class="loading-text" :style="{ color: getWaveBarColor(idx) }">Loading audio...</span>
              </div>

              <div v-if="isPlaying && idx === activeSegment" class="playing-indicator">
                <div class="wave-bars">
                  <div class="wave-bar"
                    v-for="i in 3"
                    :key="i"
                    :style="{ '--wave-color': getWaveBarColor(idx) }"></div>
                </div>
                <span class="playing-text" :style="{ color: getWaveBarColor(idx) }">
                  {{ customRecordings.has(idx) ? 'Custom Recording' : 'Speaking' }}
                </span>
              </div>
            </div>

            <!-- Regular content display when not editing -->
            <citation-renderer
              v-if="editingSegmentIndex !== idx"
              :html="segment.content"
              :is-editable="!isPlaying && idx === activeSegment"
              :segment-index="idx"
              :max-tokens="510"
              :chat-id="chatId"
              @citation-click="handleCitationClick"
              @citation-speaking="handleCitationSpeaking"
              @auto-scroll-complete="handleAutoScrollComplete"
              @full-audio-ready="handleFullAudioReady"
              @audio-progress="handleAudioProgress"
              @auto-scroll-progress="handleAutoScrollProgress"
              @content-change="updateSegmentContent"
              @content-updated="handleContentUpdated"
              @warning="showTempMessage"
            />

            <!-- AI Segment Editor when editing -->
            <AISegmentEditor
              v-if="editingSegmentIndex === idx"
              ref="aiEditor"
              :message-id="messageId"
              :segment-index="idx"
              :parsed-conversation="parsedConversation"
              :is-editing="true"
              :is-pro="isPro"
              :is-submitting="isSubmitting"
              @content-updated="handleAIContentUpdate"
              @cancel-edit="cancelEditing"
            />

            <voice-recorder
              v-if="idx === activeSegment && !isPlaying && editingSegmentIndex !== idx"
              :is-pro="isPro"
              :segment-index="idx"
              :segment="segment"
              :message-id="messageId"
              :custom-recordings="customRecordings"
              :speaker-color="getWaveBarColor(idx)"
              @recording-saved="handleRecordingSaved"
              @recording-deleted="handleRecordingDeleted"
            />
          </div>
        </transition-group>

        <!-- New segment insertion with AI -->
        <div v-if="isCreatingSegment" class="new-segment-container">
          <div class="new-segment-header">
            <h4>
              Creating New Segment
              <span class="insert-position-indicator" :class="insertPosition">
                (Inserting {{ insertPosition === 'before' ? 'Before' : 'After' }} Segment #{{ newSegmentIndex + 1 }})
              </span>
            </h4>
            <button
              class="close-btn"
              @click="cancelCreating"
              :disabled="isSubmitting"
            >×</button>
          </div>

          <AISegmentEditor
            ref="aiCreator"
            :message-id="messageId"
            :segment-index="newSegmentIndex"
            :parsed-conversation="parsedConversation"
            :is-editing="false"
            :is-pro="isPro"
            :insert-position="insertPosition"
            :is-submitting="isSubmitting"
            @content-updated="handleNewSegmentContent"
            @cancel-edit="cancelCreating"
          />
        </div>
      </div>

      <!-- Loading/Error States -->
      <div v-else-if="error" class="error-state">
        {{ error }}
      </div>
      <div v-else class="loading-state">
        Loading conversation...
      </div>
    </div>
  </div>
</template>

<script>
  import SegmentEditingButtons from './SegmentEditingButtons.vue';
  import CitationRenderer from '../CitationRenderer.vue';
  import VoiceRecorder from '../curated_content/VoiceRecorder.vue';
  import SectionTypeEditor from './SectionTypeEditor.vue';
  import AISegmentEditor from './editor/AISegmentEditor.vue';
  import API from '@/api';
  import { mapActions } from 'vuex';

export default {
  components: {
    SegmentEditingButtons,
    CitationRenderer,
    VoiceRecorder,
    SectionTypeEditor,
    AISegmentEditor
  },
  name: 'ConversationViewer',
  props: {
    parsedConversation: {
      type: Array,
      default: () => []
    },
    activeSegment: {
      type: Number,
      default: -1
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    isExpandedView: {
      type: Boolean,
      default: true
    },
    customRecordings: {
      type: Map,
      default: () => new Map()
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    messageId: {
      required: true
    },
    chatId: String,
    isPro: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isViewerExpanded: true, // Start expanded by default
      editingSegmentIndex: null,
      isCreatingSegment: false,
      newSegmentIndex: null,
      insertPosition: 'after', // Default insert position
      activeInsertDropdown: null, // Track which dropdown is open
      isSubmitting: false // Track submission state
    };
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),

    handleSectionUpdated(data) {
      // Update your local data
      if (data.index >= 0 && data.index < this.parsedConversation.length) {
        this.$set(this.parsedConversation[data.index], 'section', data.section);
      }

      // Forward event to parent if needed
      this.$emit('section-updated', data);
    },
    activateSegment(idx, event) {
      // Don't trigger when clicking on elements that have their own interactions
      if (
        event.target.closest('button') ||
        event.target.closest('a') ||
        (idx === this.activeSegment && event.target.closest('.citation-renderer[contenteditable="true"]'))
      ) {
        return;
      }

      // Don't change active segment if currently editing or submitting
      if (this.editingSegmentIndex !== null || this.isCreatingSegment || this.isSubmitting) {
        return;
      }

      // Set this segment as active
      this.$emit('set-active-segment', idx);

      // Optional: scroll the segment into view
      this.$nextTick(() => {
        const segmentEl = this.$refs[`segment_${idx}`] ||
                        document.querySelector(`.segment:nth-child(${idx + 1})`);
        if (segmentEl) {
          segmentEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    handleContentUpdated(data) {
      // Forward the event to the parent
      this.$emit('segment-content-updated', data);
    },
    updateSegmentContent({ index, content }) {
      // Update the segment content in your data structure
      if (index >= 0 && index < this.parsedConversation.length) {
        // Create a deep copy to maintain reactivity
        const updatedConversation = JSON.parse(JSON.stringify(this.parsedConversation));
        updatedConversation[index].content = content;

        // If using props from parent:
        this.$emit('update-conversation', updatedConversation);
      }
    },
    showTempMessage({ message, type }) {
      // Use the Vuex action instead of emitting
      this.showTempMessageFn({ message, type });
    },
    toggleViewer() {
      this.isViewerExpanded = !this.isViewerExpanded;
    },
    setSegmentRef(el, messageId, idx) {
      this.$emit('set-segment-ref', { el, messageId, idx });
    },
    getWaveBarColor(idx) {
      // Define colors here
      const colors = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#14B8A6', // Teal
        '#F97316'  // Orange
      ];
      return colors[idx % colors.length];
    },
    handleSegmentDeleted(data) {
      this.$emit('segment-deleted', data);
    },
    handleSegmentAdded(data) {
      this.$emit('segment-added', data);
    },
    handleCitationClick(data) {
      this.$emit('citation-click', data);
    },
    handleCitationSpeaking(data) {
      this.$emit('citation-speaking', data);
    },
    handleAutoScrollComplete(data) {
      this.$emit('auto-scroll-complete', data);
    },
    handleFullAudioReady(data) {
      this.$emit('full-audio-ready', data);
    },
    handleAudioProgress(data) {
      this.$emit('audio-progress', data);
    },
    handleAutoScrollProgress(data) {
      this.$emit('auto-scroll-progress', data);
    },
    handleRecordingSaved(data) {
      this.$emit('recording-saved', data);
    },
    handleRecordingDeleted(data) {
      this.$emit('recording-deleted', data);
    },

    // AI editing functionality
    startEditingWithAI(index) {
      if (this.isSubmitting) return; // Don't allow editing while submitting

      this.editingSegmentIndex = index;
      // Make sure this segment is the active one
      this.$emit('set-active-segment', index);
    },

    openInsertDropdown(index) {
      if (this.isSubmitting) return; // Don't allow dropdown while submitting

      // Close any open dropdown first
      if (this.activeInsertDropdown !== null) {
        this.activeInsertDropdown = null;
      } else {
        this.activeInsertDropdown = index;
      }

      // Add a click event listener to close the dropdown when clicking outside
      this.$nextTick(() => {
        const closeDropdown = (e) => {
          // Check if click is outside the dropdown
          const dropdown = document.querySelector('.insert-dropdown-menu');
          if (dropdown && !dropdown.contains(e.target) && !e.target.classList.contains('ai-insert-btn')) {
            this.activeInsertDropdown = null;
            document.removeEventListener('click', closeDropdown);
          }
        };

        document.addEventListener('click', closeDropdown);
      });
    },

    startCreatingWithAI(index, position) {
      if (this.isSubmitting) return; // Don't allow creation while submitting

      this.isCreatingSegment = true;
      this.newSegmentIndex = index;
      this.insertPosition = position || 'after'; // Default to after if not specified
      this.activeInsertDropdown = null; // Close dropdown

      // Show the creation UI
      this.$nextTick(() => {
        const newSegmentContainer = document.querySelector('.new-segment-container');
        if (newSegmentContainer) {
          newSegmentContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },

    cancelEditing() {
      if (this.isSubmitting) return; // Don't allow cancellation while submitting
      this.editingSegmentIndex = null;
    },

    cancelCreating() {
      if (this.isSubmitting) return; // Don't allow cancellation while submitting
      this.isCreatingSegment = false;
      this.newSegmentIndex = null;
      this.insertPosition = 'after'; // Reset to default
      this.activeInsertDropdown = null; // Close any open dropdowns
    },

    async handleAIContentUpdate(data) {
      try {
        // Set submitting state
        this.isSubmitting = true;

        // Extract content from data
        let contentToSave = data.content;

        // Check if content is a JSON string containing 'new_content'
        if (typeof contentToSave === 'string' && contentToSave.trim().startsWith('{') && contentToSave.includes('new_content')) {
          try {
            const contentObj = JSON.parse(contentToSave);
            if (contentObj.new_content) {
              contentToSave = contentObj.new_content;
            }
          } catch (e) {
            // If parsing fails, use the original string
            console.warn('Could not parse JSON content, using as-is', e);
          }
        }

        // Prepare content for saving
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentToSave;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        const contentPayload = {
          content: plainText
        };

        // Use the API service consistently
        const response = await API.post(
          `/user_customization/messages/${this.messageId}/segments/${data.index}/content`,
          contentPayload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          // Update the local conversation
          const updatedConversation = JSON.parse(JSON.stringify(this.parsedConversation));
          updatedConversation[data.index].content = plainText;
          updatedConversation[data.index].edited = true;

          // Update reactively
          this.$set(this.parsedConversation, data.index, updatedConversation[data.index]);

          // Emit update to parent
          this.$emit('update-conversation', updatedConversation);

          // Show success message
          this.showTempMessageFn({
            message: "Segment updated with AI-generated content successfully.",
            type: "success",
            timeout: 3000
          });

          // Exit editing mode
          this.editingSegmentIndex = null;

          // Reset the AISegmentEditor component
          this.$nextTick(() => {
            const aiEditor = this.$refs.aiEditor;
            if (aiEditor && typeof aiEditor.resetAfterSave === 'function') {
              aiEditor.resetAfterSave();
            }
          });
        } else {
          throw new Error('Failed to update segment content');
        }
      } catch (error) {
        console.error('Error updating segment with AI content:', error);
        this.showTempMessageFn({
          message: "Failed to update segment: " + (error.message || "Unknown error"),
          type: "error",
          timeout: 5000
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async handleNewSegmentContent(data) {
      try {
        // Track submitting state
        this.isSubmitting = true;

        // Extract content from data
        let contentToSave = data.content;

        // Check if content is a JSON string containing 'new_content'
        if (typeof contentToSave === 'string' && contentToSave.trim().startsWith('{') && contentToSave.includes('new_content')) {
          try {
            const contentObj = JSON.parse(contentToSave);
            if (contentObj.new_content) {
              contentToSave = contentObj.new_content;
            }
          } catch (e) {
            // If parsing fails, use the original string
            console.warn('Could not parse JSON content, using as-is', e);
          }
        }

        // Prepare the plain text content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentToSave;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        // Get reference segment for section
        const referenceSegment = this.parsedConversation[this.newSegmentIndex];

        // Extract speaker, role, and section from data or use defaults
        const speaker = data.speaker || (referenceSegment?.speaker || "Speaker");
        const role = data.role || (referenceSegment?.role || "Role");
        const section = data.section || (referenceSegment?.section || "main");

        // Create the insertion payload - matching the exact format required
        const payload = {
          position: this.insertPosition,
          content: plainText,
          speaker: speaker,
          role: role,
          section: section
        };

        // Call the insert endpoint
        const response = await API.post(
          `/user_customization/messages/${this.messageId}/segments/${this.newSegmentIndex}/insert`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          // If the server returns the full updated response, use it
          if (response.data.updated_response) {
            try {
              const updatedData = JSON.parse(response.data.updated_response);
              if (updatedData.conversation) {
                // Emit update to parent with full conversation
                this.$emit('update-conversation', updatedData.conversation);
              }
            } catch (e) {
              console.warn('Could not parse updated response, using manual update', e);

              // Fall back to manual update using the new segment info
              if (response.data.new_segment && response.data.insert_position !== undefined) {
                const updatedConversation = JSON.parse(JSON.stringify(this.parsedConversation));
                updatedConversation.splice(response.data.insert_position, 0, response.data.new_segment);
                this.$emit('update-conversation', updatedConversation);
              }
            }
          } else if (response.data.new_segment && response.data.insert_position !== undefined) {
            // Manually update with the new segment info
            const updatedConversation = JSON.parse(JSON.stringify(this.parsedConversation));
            updatedConversation.splice(response.data.insert_position, 0, response.data.new_segment);
            this.$emit('update-conversation', updatedConversation);
          }

          // Emit the segment added event to parent component
          this.$emit('segment-added', {
            position: this.insertPosition,
            index: this.newSegmentIndex,
            insertedAt: response.data.insert_position,
            segment: response.data.new_segment,
            newSegmentCount: response.data.new_segment_count,
            updatedResponse: response.data.updated_response
          });

          // Show success message
          this.showTempMessageFn({
            message: `New segment inserted ${this.insertPosition} segment #${this.newSegmentIndex + 1} successfully.`,
            type: "success",
            timeout: 3000
          });

          // Reset state
          this.isCreatingSegment = false;
          this.newSegmentIndex = null;
          this.insertPosition = 'after';
          this.activeInsertDropdown = null;

          // Reset the AISegmentEditor component
          this.$nextTick(() => {
            const aiCreator = this.$refs.aiCreator;
            if (aiCreator && typeof aiCreator.resetAfterSave === 'function') {
              aiCreator.resetAfterSave();
            }

            // Make the new segment active if possible
            const newActiveIndex = response.data.insert_position || this.newSegmentIndex;
            if (newActiveIndex !== undefined) {
              this.$emit('set-active-segment', newActiveIndex);
            }
          });
        } else {
          throw new Error('Failed to insert new segment');
        }
      } catch (error) {
        console.error('Error adding new segment with AI content:', error);
        this.showTempMessageFn({
          message: "Failed to add new segment: " + (error.message || "Unknown error"),
          type: "error",
          timeout: 5000
        });
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>


<style scoped>
  .conversation-viewer {
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .theme--dark .conversation-viewer {
    background-color: #1a202c;
  }

  .viewer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .viewer-title-area {
    display: flex;
    align-items: center;
  }

  .viewer-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
  }

  .theme--dark .viewer-title {
    color: #e2e8f0;
  }

  .segment-count {
    font-size: 0.75rem;
    color: #718096;
    margin-left: 0.25rem;
  }

  .toggle-btn {
    padding: 0.25rem 0.375rem;
    font-size: 0.875rem;
    color: #718096;
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .theme--dark .toggle-btn {
    border-color: #2d3748;
    color: #a0aec0;
  }

  .toggle-btn:hover {
    background-color: #f7fafc;
  }

  .theme--dark .toggle-btn:hover {
    background-color: #2d3748;
  }

  .viewer-content {
    margin-top: 0.75rem;
  }

  .conversation {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding: 1rem;
    background-color: white;
    border-radius: 8px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .theme--dark .conversation {
    background-color: #0f172a;
  }

  .segment {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    transition: all 0.2s ease;
    background-color: #ffffff;
    position: relative;
  }

  .theme--dark .segment {
    border-color: #2d3748;
    background-color: #1e293b;
  }

  .segment.active {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  .segment.is-playing {
    background-color: #f0f9ff;
  }

  .theme--dark .segment.is-playing {
    background-color: #1e3a8a;
  }

  .speaker-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .speaker-name {
    font-weight: 600;
    font-size: 0.9rem;
  }

  .segment-number {
    font-size: 0.75rem;
    color: #718096;
  }

  .speaker-role {
    font-size: 0.75rem;
    color: #718096;
    font-style: italic;
  }

  .custom-voice-badge {
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }

  .edited-badge {
    font-size: 0.75rem;
    margin-left: 0.25rem;
    opacity: 0.8;
    cursor: help;
  }

  .modern-edit-indicator {
    margin-left: auto;
    padding: 0.25rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .modern-edit-indicator:hover {
    transform: scale(1.1);
  }

  .loading-indicator,
  .playing-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #e2e8f0;
    border-top: 2px solid;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .loading-text,
  .playing-text {
    font-size: 0.75rem;
  }

  .wave-bars {
    display: flex;
    align-items: flex-end;
    height: 16px;
    gap: 2px;
  }

  .wave-bar {
    width: 4px;
    height: 4px;
    border-radius: 2px;
    background-color: var(--wave-color);
    animation: wave 1s ease-in-out infinite;
  }

  .wave-bar:nth-child(1) { animation-delay: 0s; }
  .wave-bar:nth-child(2) { animation-delay: 0.1s; }
  .wave-bar:nth-child(3) { animation-delay: 0.2s; }

  @keyframes wave {
    0%, 100% { height: 4px; }
    50% { height: 16px; }
  }

  .error-state,
  .loading-state {
    text-align: center;
    padding: 2rem;
    color: #718096;
    font-size: 0.875rem;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  /* Segment editing state */
  .segment.is-editing {
    border-color: #8b5cf6;
    background-color: #f5f3ff;
  }

  .theme--dark .segment.is-editing {
    background-color: #2e1065;
    border-color: #a78bfa;
  }

  /* AI editing buttons */
  .segment-editing-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .ai-editing-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .ai-edit-btn, .ai-insert-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .ai-edit-btn {
    background-color: #8b5cf6;
    color: white;
    border: none;
  }

  .ai-edit-btn:hover:not(:disabled) {
    background-color: #7c3aed;
  }

  .ai-insert-btn {
    background-color: white;
    border: 1px solid #e2e8f0;
    color: #64748b;
  }

  .ai-insert-btn:hover:not(:disabled) {
    background-color: #f8fafc;
    color: #475569;
  }

  .theme--dark .ai-insert-btn {
    background-color: #1e293b;
    border-color: #475569;
    color: #94a3b8;
  }

  .theme--dark .ai-insert-btn:hover:not(:disabled) {
    background-color: #334155;
    color: #e2e8f0;
  }

  .ai-edit-btn:disabled, .ai-insert-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .icon-ai, .icon-plus {
    font-size: 0.75rem;
  }

  .icon-ai::before {
    content: "✨"; /* AI sparkle */
  }

  .icon-plus::before {
    content: "+"; /* Plus sign */
  }

  /* Insert dropdown menu */
  .ai-insert-dropdown {
    position: relative;
  }

  .insert-dropdown-menu {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    z-index: 10;
    min-width: 150px;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 0.25rem;
  }

  .theme--dark .insert-dropdown-menu {
    background-color: #1e293b;
    border-color: #334155;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  .insert-option {
    display: block;
    width: 100%;
    text-align: left;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    border: none;
    background: none;
    border-radius: 4px;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .insert-option:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }

  .insert-option.insert-before {
    border-left: 2px solid #3b82f6;
  }

  .insert-option.insert-after {
    border-left: 2px solid #10b981;
  }

  .theme--dark .insert-option {
    color: #e2e8f0;
  }

  .theme--dark .insert-option:hover {
    background-color: #334155;
    color: #ffffff;
  }

  /* New segment container */
  .new-segment-container {
    background-color: #f8fafc;
    border: 1px dashed #8b5cf6;
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
    position: relative;
  }

  .theme--dark .new-segment-container {
    background-color: #1e293b;
    border-color: #a78bfa;
  }

  .new-segment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .new-segment-header h4 {
    margin: 0;
    font-size: 0.875rem;
    color: #8b5cf6;
  }

  .theme--dark .new-segment-header h4 {
    color: #c4b5fd;
  }

  .insert-position-indicator {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: normal;
    color: #64748b;
    margin-left: 0.5rem;
    padding: 0.125rem 0.375rem;
    border-radius: 3px;
    background-color: #e2e8f0;
  }

  .insert-position-indicator.before {
    border-left: 2px solid #3b82f6;
    color: #3b82f6;
  }

  .insert-position-indicator.after {
    border-left: 2px solid #10b981;
    color: #10b981;
  }

  .theme--dark .insert-position-indicator {
    color: #94a3b8;
    background-color: #334155;
  }

  .theme--dark .insert-position-indicator.before {
    color: #60a5fa;
  }

  .theme--dark .insert-position-indicator.after {
    color: #34d399;
  }

  .close-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #f1f5f9;
    border: none;
    color: #64748b;
    font-size: 1rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .close-btn:hover:not(:disabled) {
    background-color: #e2e8f0;
    color: #475569;
  }

  .close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .theme--dark .close-btn {
    background-color: #334155;
    color: #94a3b8;
  }

  .theme--dark .close-btn:hover:not(:disabled) {
    background-color: #475569;
    color: #e2e8f0;
  }

  /* Pro feature indicators */
  .pro-badge {
    background-color: #8b5cf6;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .conversation {
      padding: 0.5rem;
    }

    .segment {
      padding: 0.75rem;
    }

    .speaker-info {
      flex-wrap: wrap;
    }
  }
  </style>
